import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, normalizeStyle as _normalizeStyle, Fragment as _Fragment } from "vue";
const _hoisted_1 = { class: "ad-intro" };
const _hoisted_2 = { class: "ad-subtitle" };
const _hoisted_3 = {
    key: 0,
    class: "ad-section"
};
const _hoisted_4 = {
    key: 1,
    class: "ad-section"
};
const _hoisted_5 = {
    key: 0,
    class: "ad-preview"
};
const _hoisted_6 = { class: "ad-preview-value" };
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useAppState } from '@/composables/useAppState';
import { useCustomer } from '@/composables/useCustomer';
import SimpleDrawerPanel from './SimpleDrawerPanel.vue';
import AddressLookup from './AddressLookup.vue';
import InputSections from './InputSections.vue';
import MGButton from './MGButton.vue';
import SuccessToast from './SuccessToast.vue';
export default /*@__PURE__*/ _defineComponent({
    __name: 'AddressDrawer',
    props: {
        modelValue: { type: Boolean },
        subtitle: { default: '' }
    },
    emits: ["update:modelValue", "saved"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const state = useAppState();
        const { getCustomer, addAddress } = useCustomer();
        const existing = computed(() => state.customer?.person?.addresses?.[0] || null);
        const displaySubtitle = computed(() => props.subtitle ||
            `We need your full address${existing.value?.postCode ? ' for ' + existing.value.postCode.toUpperCase() : ''} in order for you to pay by bank.`);
        const address = ref(null);
        const manual = ref(false);
        const saving = ref(false);
        const toastCount = ref(0);
        const inputSections = ref(null);
        const manualSections = ref([]);
        // Height animation: measure the inner content and drive the outer wrapper's height
        const inner = ref(null);
        const outerHeight = ref('auto');
        let observer = null;
        function measure() {
            if (inner.value)
                outerHeight.value = inner.value.scrollHeight + 'px';
        }
        watch(inner, (el) => {
            observer?.disconnect();
            if (el) {
                observer = new ResizeObserver(measure);
                observer.observe(el);
                nextTick(measure);
            }
            else {
                outerHeight.value = 'auto';
            }
        });
        onUnmounted(() => observer?.disconnect());
        onMounted(() => getCustomer());
        const formattedAddress = computed(() => {
            if (!address.value)
                return '';
            const a = address.value;
            return [a.line1, a.line2, a.line3, a.town, a.postCode].filter(Boolean).join(', ');
        });
        const canSave = computed(() => {
            if (manual.value) {
                return !!(findFieldValue('Address line 1') && findFieldValue('Town/City') && findFieldValue('Postcode'));
            }
            return !!(address.value && address.value.postCode);
        });
        function enableManual() {
            const a = address.value || {};
            manualSections.value = [{
                    fields: [
                        { label: 'Address line 1', type: 'text', value: a.line1 || '', required: true, validationText: 'Please enter the first line of your address' },
                        { label: 'Address line 2', type: 'text', value: a.line2 || '' },
                        { label: 'Town/City', type: 'text', value: a.town || '', required: true, validationText: 'Please enter your town or city' },
                        { label: 'Postcode', type: 'text', value: a.postCode || '', required: true, validationText: 'Please enter your postcode' },
                    ]
                }];
            manual.value = true;
        }
        function findFieldValue(label) {
            for (const section of manualSections.value) {
                for (const field of section.fields) {
                    if (field.label === label)
                        return field.value || null;
                }
            }
            return null;
        }
        function buildAddress() {
            if (!manual.value) {
                const a = address.value;
                return { line1: a.line1 || null, line2: a.line2 || null, line3: a.line3 || null, town: a.town || null, postCode: a.postCode || null };
            }
            return {
                line1: findFieldValue('Address line 1'),
                line2: findFieldValue('Address line 2'),
                line3: null,
                town: findFieldValue('Town/City'),
                postCode: findFieldValue('Postcode'),
            };
        }
        function save() {
            if (!canSave.value)
                return;
            saving.value = true;
            const built = buildAddress();
            addAddress(built, () => {
                getCustomer(null, false);
                saving.value = false;
                toastCount.value = 1;
                emit('saved', built);
                emit('update:modelValue', false);
            });
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(SimpleDrawerPanel, {
                    modelValue: _ctx.modelValue,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => (_ctx.$emit('update:modelValue', $event))),
                    title: "Where do you live?",
                    closeButton: true,
                    divider: false
                }, {
                    footer: _withCtx(() => [
                        _createVNode(MGButton, {
                            text: "Save address",
                            disabled: !canSave.value || saving.value,
                            fullWidth: true,
                            onClick: save
                        }, null, 8, ["disabled"])
                    ]),
                    default: _withCtx(() => [
                        _createElementVNode("div", {
                            class: "ad-outer",
                            style: _normalizeStyle({ height: outerHeight.value })
                        }, [
                            _createElementVNode("div", {
                                ref_key: "inner",
                                ref: inner,
                                class: "ad-content"
                            }, [
                                _createElementVNode("div", _hoisted_1, [
                                    _createElementVNode("p", _hoisted_2, _toDisplayString(displaySubtitle.value), 1)
                                ]),
                                (!manual.value)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                                        _createVNode(AddressLookup, {
                                            theme: "light",
                                            value: existing.value,
                                            modelValue: address.value,
                                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((address).value = $event))
                                        }, null, 8, ["value", "modelValue"]),
                                        _createElementVNode("div", {
                                            class: "ad-manual-link",
                                            onClick: enableManual
                                        }, "Can't find it? Enter manually")
                                    ]))
                                    : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                        _createVNode(InputSections, {
                                            ref_key: "inputSections",
                                            ref: inputSections,
                                            sections: manualSections.value,
                                            theme: "light"
                                        }, null, 8, ["sections"]),
                                        _createElementVNode("div", {
                                            class: "ad-manual-link",
                                            onClick: _cache[1] || (_cache[1] = ($event) => (manual.value = false))
                                        }, "Search by postcode instead")
                                    ])),
                                _createVNode(_Transition, null, {
                                    default: _withCtx(() => [
                                        (!manual.value && address.value)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                                _cache[4] || (_cache[4] = _createElementVNode("div", { class: "ad-preview-label" }, "Selected address", -1)),
                                                _createElementVNode("div", _hoisted_6, _toDisplayString(formattedAddress.value), 1)
                                            ]))
                                            : _createCommentVNode("", true)
                                    ]),
                                    _: 1
                                })
                            ], 512)
                        ], 4)
                    ]),
                    _: 1
                }, 8, ["modelValue"]),
                _createVNode(SuccessToast, {
                    count: toastCount.value,
                    "onUpdate:count": _cache[3] || (_cache[3] = ($event) => ((toastCount).value = $event)),
                    message: "Address updated"
                }, null, 8, ["count"])
            ], 64));
        };
    }
});
